<template>
    <input-child
        v-bind="
            getInputProps({
                icon: loading && iconSide === 'append' ? iconLoading : icon,
                iconRight:
                    loading && iconSide !== 'append' ? iconLoading : iconRight,

                onFocus: onInputFocus,
                onBlur: onInputBlur,
                onClick: onInputFocus,
                onKeyDown: onInputWrite,
                inputClass: inputClass,
                id: getUniqueId('inputAutocomplete'),
                dropdownOpen: dropdownShow,
                error: inputError,
            })
        "
        v-model="inputValue"
        ref="input"
    >
        <template #beforeInput>
            <slot name="beforeInput"></slot>
        </template>
        <template #afterInput>
            <slot name="afterInput"></slot>
        </template>
        <template #inputContainer>
            <div
                v-if="!hideDropdown && dropdownShow && autocompleteList.length"
                class="absolute top-full left-0 right-0 z-[1000] bg-white border border-t-0 border-gray-200 rounded-b-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200"
            >
                <div class="max-h-60 overflow-y-auto">
                    <template v-for="(item, idx) in autocompleteList">
                        <div
                            class="px-5 py-3 text-sm font-bold text-primary hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                            @mousedown.prevent="onSelectItem(item, $event)"
                        >
                            {{ item.text }}
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <template #hidden>
            <slot
                name="hidden"
                v-bind="{ autocompleteList, selected, dropdownShow }"
            >
            </slot>
        </template>
        <template #next>
            <slot
                name="next"
                v-bind="{ autocompleteList, selected, dropdownShow }"
            >
            </slot>
        </template>
        <template #prev>
            <slot
                name="prev"
                v-bind="{ autocompleteList, selected, dropdownShow }"
            >
            </slot>
        </template>
    </input-child>
</template>

<script>
import mixinCommon from "./mixinCommon.vue";
import mixinAjax from "./mixinAjax.vue";
import mixinInput from "./mixinInput.vue";

import Input from "./Input.vue";

const { props } = Input;

export default {
    mixins: [mixinCommon, mixinAjax, mixinInput],

    components: {
        "input-child": Input,
    },
    props: {
        ...props,

        iconLoading: {
            type: String,
            default: "fa fa-spinner fa-pulse text-primary",
        },

        strictSelection: {
            type: [Boolean, String, Number],
            default: false,
        },
        strictSelectionErrorMessage: {
            type: String,
            default: "Please select a valid option from the list.",
        },
        autocompleteOnVoid: {
            type: [String, Number, Boolean],
            default: true,
        },

        autocompleteHandler: {
            type: Function,
            default: null,
        },

        disableAjax: {
            type: [Boolean, String, Number],
            default: null,
        },

        onSelect: {
            type: Function,
            default: () => {},
        },

        defaultSelected: {
            type: Object,
            default: () => null,
        },

        defaultAutocompleteList: {
            type: Array,
            default: () => [],
        },
        hideDropdown: {
            type: [Boolean, String, Number],
            default: false,
        },
    },

    data() {
        return {
            timer: null,
            dropdownShow: false,
            selected: this.defaultSelected,
            autocompleteList: this.defaultAutocompleteList,
            autocompleteCache: {},
            strictSelectionError: "",
            preserveInputOnSelectedWatcher: false,
        };
    },
    computed: {
        isStrictSelectionEnabled() {
            return (
                this.strictSelection === true ||
                this.strictSelection === "true" ||
                !!+this.strictSelection
            );
        },
        inputError() {
            return this.error || this.strictSelectionError;
        },
        firstAutocomplete() {
            if (!this.autocompleteList.length) {
                return false;
            }

            return this.autocompleteList[0];
        },
    },
    watch: {
        ajaxLoading(isLoading) {
            this.loading = !!+isLoading;
        },

        inputValue(inputValue) {
            this.$emit("input", inputValue);
            this.setNativeInputValidationMessage("");
            if (this.strictSelectionError) {
                this.strictSelectionError = "";
            }
        },
        defaultAutocompleteList: {
            handler(defaultAutocompleteList) {
                this.autocompleteList = defaultAutocompleteList;
            },
            deep: true,
        },
        selected: {
            handler(item = null) {
                const preserveInput = this.preserveInputOnSelectedWatcher;
                this.preserveInputOnSelectedWatcher = false;
                const { text = "" } = item || {};
                if (!preserveInput) {
                    this.inputValue = text;
                }
                this.onSelect(item);
            },
            deep: true,
        },
    },
    mounted() {},
    methods: {
        onSelectItem(item, evt) {
            evt.preventDefault();
            this.selectItem(item);
            this.dropdownShow = false;
        },

        selectItem(item, { preserveInput = false } = {}) {
            this.preserveInputOnSelectedWatcher = preserveInput;
            this.selected = item || null;
            if (!preserveInput) {
                this.inputValue = item ? item.text : "";
            }
            this.clearStrictSelectionError();
        },

        matchToAutocomplete(pos = null) {
            if (!this.autocompleteList.length) {
                return false;
            }
            if (pos >= 0 && this.autocompleteList.length < pos + 1) {
                return false;
            }
            const list = this.autocompleteList[pos]
                ? [this.autocompleteList[pos]]
                : this.autocompleteList;
            for (let item of list) {
                if (
                    this.inputValue.toLowerCase().trim() ===
                    item.text.toLowerCase().trim()
                ) {
                    return item;
                }
            }
            return false;
        },
        matchToSelected() {
            if (!this.selected) {
                return false;
            }
            return (
                this.inputValue.toLowerCase().trim() ===
                this.selected.text.toLowerCase().trim()
            );
        },

        async fetchAutocomplete() {
            if (!!+this.disableAjax) {
                return;
            }

            const queryKey = String(this.inputValue || "").trim().toLowerCase();
            if (!queryKey && !+this.autocompleteOnVoid) {
                return this.resetAutocomplete();
            }

            if (Object.prototype.hasOwnProperty.call(this.autocompleteCache, queryKey)) {
                this.autocompleteList = this.autocompleteCache[queryKey];
                return this.autocompleteList;
            }

            if (typeof this.autocompleteHandler === "function") {
                return await this.autocompleteHandler();
            }

            await this.callAjax({ query: this.inputValue });
            if (this.isBlur()) {
                return this.resetAutocomplete();
            }

            const data = Array.isArray(this.responseData) ? this.responseData : [];
            this.autocompleteCache[queryKey] = data;
            const cacheKeys = Object.keys(this.autocompleteCache);
            if (cacheKeys.length > 30) {
                delete this.autocompleteCache[cacheKeys[0]];
            }

            return (this.autocompleteList = data);
        },

        async onInputWrite(evt) {
            if (this.timer) clearTimeout(this.timer);
            this.clearStrictSelectionError();
            switch (evt.which) {
                case 13:
                    evt.preventDefault();
                    await this.fetchAutocomplete();
                    return this.blur();
                    break;
            }
            this.timer = setTimeout(async () => {
                this.dropdownShow = this.isFocus();

                await this.fetchAutocomplete();
            }, 400);
        },

        async onInputFocus(evt) {
            this.select();
            this.clearStrictSelectionError();
            const isValidLength = this.length || !!+this.autocompleteOnVoid;
            if (isValidLength) {
                await this.fetchAutocomplete();
                this.dropdownShow = isValidLength && this.isFocus();
            }
        },

        onInputBlur(evt) {
            this.dropdownShow = false;
            if (this.length === 0) {
                this.selectItem(null);
            } else if (!this.matchToSelected()) {
                const itemMatch = this.matchToAutocomplete();
                if (this.isStrictSelectionEnabled) {
                    if (itemMatch) {
                        this.selectItem(itemMatch);
                    } else {
                        this.selectItem(null, { preserveInput: true });
                        this.setStrictSelectionError();
                    }
                } else {
                    this.selectItem(itemMatch || { text: this.inputValue });
                }
            }
            this.resetAutocomplete();
        },

        checkIsValid() {
            if (!this.isStrictSelectionEnabled) {
                return !this.inputError;
            }

            if (!this.length) {
                this.clearStrictSelectionError();
                return true;
            }

            if (this.matchToSelected()) {
                this.clearStrictSelectionError();
                return true;
            }

            const itemMatch = this.matchToAutocomplete();
            if (itemMatch) {
                this.selectItem(itemMatch);
                return true;
            }

            this.selectItem(null, { preserveInput: true });
            this.setStrictSelectionError();
            return false;
        },

        getNativeInputElement() {
            if (
                this.$refs.input &&
                this.$refs.input.$refs &&
                this.$refs.input.$refs.input
            ) {
                return this.$refs.input.$refs.input;
            }
            return null;
        },
        setNativeInputValidationMessage(message = "") {
            const input = this.getNativeInputElement();
            if (input && typeof input.setCustomValidity === "function") {
                input.setCustomValidity(message);
            }
        },
        setStrictSelectionError() {
            this.strictSelectionError = this.strictSelectionErrorMessage;
            this.setNativeInputValidationMessage(this.strictSelectionErrorMessage);
        },
        clearStrictSelectionError() {
            if (!this.strictSelectionError) {
                this.setNativeInputValidationMessage("");
                return;
            }
            this.strictSelectionError = "";
            this.setNativeInputValidationMessage("");
        },

        resetAutocomplete() {
            return (this.autocompleteList = this.defaultAutocompleteList || []);
        },

        getInputProps(overrideProps = {}) {
            return this.getComponentProps(Input, overrideProps);
        },
    },
};
</script>

<style scoped>
.animate-in {
    animation-duration: 0.2s;
    animation-fill-mode: both;
}
@keyframes fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
@keyframes slide-in-from-top-1 {
    from {
        transform: translateY(-0.25rem);
    }
    to {
        transform: translateY(0);
    }
}
.fade-in {
    animation-name: fade-in;
}
.slide-in-from-top-1 {
    animation-name: slide-in-from-top-1;
}
</style>
